import React, { useRef, useMemo, useLayoutEffect } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { ZONES, WORLD } from '../config/zones';
import { mulberry32 } from './rng';
import { shared } from './shared';
import { useStore } from '../store';

const HIVES = ZONES.map((z) => new THREE.Vector3(z.x, 0, -60)); // Scholars study these
const FORTS = ZONES.map((z) => new THREE.Vector3(z.x, 0, 60)); // Sentinels guard these
const dummy = new THREE.Object3D();
const agent = new THREE.Object3D();
const M = new THREE.Matrix4();
const partObj = new THREE.Object3D();

// Distinct attire silhouettes per ideological cluster (each part = one InstancedMesh).
const ATTIRE = {
  scholarly: [
    { shape: 'cyl', args: [0.17, 0.17, 0.14, 8], pos: [0, 1.86, 0], color: '#141428' },
    { shape: 'box', args: [0.62, 0.05, 0.62], pos: [0, 1.98, 0], color: '#141428' },
    { shape: 'sphere', args: [0.05, 6, 6], pos: [0.3, 1.99, 0.3], color: '#D4AF37', emissive: '#D4AF37', ei: 0.6 },
  ],
  professional: [
    { shape: 'box', args: [0.98, 0.62, 0.6], pos: [0, 1.2, 0], color: '#1e2a44' },   // tailored blazer
    { shape: 'box', args: [0.26, 0.56, 0.06], pos: [0, 1.2, 0.3], color: '#f1f5f9' }, // crisp dress shirt
    { shape: 'box', args: [0.09, 0.34, 0.04], pos: [0, 1.16, 0.33], color: '#334155' }, // slim tie
  ],
  corporate: [
    { shape: 'box', args: [0.34, 0.28, 0.14], pos: [0.46, 0.95, 0.1], color: '#241608' },
    { shape: 'box', args: [0.12, 0.45, 0.06], pos: [0, 1.15, 0.3], color: '#7a1020' },
    { shape: 'box', args: [0.9, 0.5, 0.5], pos: [0, 1.25, 0], color: '#1f2937' },
  ],
  sentinel: [
    { shape: 'sphere', args: [0.4, 10, 8], pos: [0, 1.78, 0], color: '#2b2f36' },
    { shape: 'box', args: [0.1, 0.78, 0.55], pos: [-0.48, 1.0, 0.12], color: '#3a3f47' },
    { shape: 'cyl', args: [0.05, 0.05, 0.95, 5], pos: [0.48, 1.0, 0.1], color: '#20242a', rot: [0.25, 0, 0] },
  ],
};

function AGeo({ shape, args }) {
  if (shape === 'sphere') return <sphereGeometry args={args} />;
  if (shape === 'cyl') return <cylinderGeometry args={args} />;
  return <boxGeometry args={args} />;
}

// One faction of instanced humans (bodies + heads + attire) with lightweight AI.
// `zoneIdxs` clusters the crowd around specific ideological clearings (by zone index).
function Faction({ count, seed, zMin, zMax, bodyColor, headColor, leftBehavior, style = 'plain', zoneIdxs = null }) {
  const bodyRef = useRef();
  const headRef = useRef();
  const attireRefs = useRef([]);
  const left = leftBehavior;
  const attire = ATTIRE[style] || [];

  const agents = useMemo(() => {
    const rand = mulberry32(seed);
    return Array.from({ length: count }).map(() => {
      const homeX = zoneIdxs ? ZONES[zoneIdxs[Math.floor(rand() * zoneIdxs.length)]].x : null;
      const x = homeX != null
        ? THREE.MathUtils.clamp(homeX + (rand() - 0.5) * 56, WORLD.xMin + 20, WORLD.xMax - 20)
        : WORLD.xMin + 20 + rand() * (WORLD.xMax - WORLD.xMin - 40);
      const z = zMin + rand() * (zMax - zMin);
      const special = rand() < 0.45;
      let role;
      if (style === 'scholarly') role = 'study';
      else if (style === 'professional') role = 'work';
      else role = special ? (left ? 'study' : 'patrol') : 'wander';
      return {
        pos: new THREE.Vector3(x, 0, z),
        target: new THREE.Vector3(x, 0, z),
        yaw: rand() * Math.PI * 2,
        phase: rand() * Math.PI * 2,
        speed: 1.2 + rand() * 1.4,
        angle: rand() * Math.PI * 2,
        r: 5 + rand() * 5,
        homeX,
        anchor: homeX != null ? new THREE.Vector3(homeX, 0, -60) : (left ? HIVES : FORTS)[Math.floor(rand() * ZONES.length)],
        role,
        elephantIdx: Math.floor(rand() * 999),
        retargetAt: 0,
      };
    });
  }, [count, seed, zMin, zMax, left, style]); // eslint-disable-line react-hooks/exhaustive-deps

  const attireMats = useMemo(() => attire.map((pt) => {
    partObj.position.set(...pt.pos);
    partObj.rotation.set(...(pt.rot || [0, 0, 0]));
    partObj.scale.set(1, 1, 1);
    partObj.updateMatrix();
    return partObj.matrix.clone();
  }), [attire]);

  useLayoutEffect(() => {
    if (!bodyRef.current || !headRef.current) return;
    const bc = new THREE.Color(bodyColor);
    const hc = new THREE.Color(headColor);
    for (let i = 0; i < count; i++) {
      bodyRef.current.setColorAt(i, bc);
      headRef.current.setColorAt(i, hc);
    }
    if (bodyRef.current.instanceColor) bodyRef.current.instanceColor.needsUpdate = true;
    if (headRef.current.instanceColor) headRef.current.instanceColor.needsUpdate = true;
  }, [count, bodyColor, headColor]);

  useFrame((state, delta) => {
    if (!bodyRef.current || !headRef.current) return;
    for (let p = 0; p < attire.length; p++) if (!attireRefs.current[p]) return;
    const dt = Math.min(delta, 0.05);
    const now = state.clock.elapsedTime;
    const elephants = shared.herds.elephants;

    for (let i = 0; i < count; i++) {
      const a = agents[i];

      // Stealth Alert: freeze and nervously look around when a predator prowls within ~5m
      let bd2 = 25;
      for (const pr of shared.predators) { const dx = a.pos.x - pr.x, dz = a.pos.z - pr.z; const dd = dx * dx + dz * dz; if (dd < bd2) bd2 = dd; }
      if (bd2 < 25) { if (now >= (a.cautionUntil || 0)) a.baseYaw = a.yaw; a.cautionUntil = now + 1.6; }
      const cautious = now < (a.cautionUntil || 0);

      if (cautious) {
        a.yaw = (a.baseYaw != null ? a.baseYaw : a.yaw) + Math.sin(now * 5 + a.phase) * 0.9;
      } else if (a.role === 'study') {
        a.angle += dt * 0.35;
        a.target.set(a.anchor.x + Math.cos(a.angle) * a.r, 0, a.anchor.z + Math.sin(a.angle) * a.r);
      } else if (a.role === 'work') {
        // Professionals circulate within their clearing (Media / Tech / Economy)
        if (now > a.retargetAt || a.target.distanceTo(a.pos) < 1.5) {
          a.retargetAt = now + 3 + Math.random() * 4;
          const hx = a.homeX ?? 0;
          a.target.set(
            THREE.MathUtils.clamp(hx + (Math.random() - 0.5) * 46, WORLD.xMin + 20, WORLD.xMax - 20),
            0,
            zMin + Math.random() * (zMax - zMin)
          );
        }
      } else if (a.role === 'patrol' && elephants.length) {
        const e = elephants[a.elephantIdx % elephants.length];
        a.target.set(e.x + Math.cos(a.angle) * 5, 0, e.z + Math.sin(a.angle) * 5 - 3);
      } else {
        if (now > a.retargetAt || a.target.distanceTo(a.pos) < 1.5) {
          a.retargetAt = now + 3 + Math.random() * 4;
          a.target.set(
            WORLD.xMin + 20 + Math.random() * (WORLD.xMax - WORLD.xMin - 40),
            0,
            zMin + Math.random() * (zMax - zMin)
          );
        }
      }

      const dx = a.target.x - a.pos.x;
      const dz = a.target.z - a.pos.z;
      const d = Math.hypot(dx, dz);
      if (!cautious && d > 0.1) {
        a.pos.x += (dx / d) * Math.min(a.speed * dt, d);
        a.pos.z += (dz / d) * Math.min(a.speed * dt, d);
        a.yaw = THREE.MathUtils.lerp(a.yaw, Math.atan2(dx, dz), 0.12);
      }
      a.phase += dt * 6;
      const bob = Math.abs(Math.sin(a.phase)) * 0.12;

      dummy.position.set(a.pos.x, 0.9 + bob, a.pos.z);
      dummy.rotation.set(0, a.yaw, 0);
      dummy.scale.setScalar(1);
      dummy.updateMatrix();
      bodyRef.current.setMatrixAt(i, dummy.matrix);

      dummy.position.set(a.pos.x, 1.7 + bob, a.pos.z);
      dummy.updateMatrix();
      headRef.current.setMatrixAt(i, dummy.matrix);

      if (attire.length) {
        agent.position.set(a.pos.x, bob, a.pos.z);
        agent.rotation.set(0, a.yaw, 0);
        agent.scale.setScalar(1);
        agent.updateMatrix();
        for (let p = 0; p < attire.length; p++) {
          M.multiplyMatrices(agent.matrix, attireMats[p]);
          attireRefs.current[p].setMatrixAt(i, M);
        }
      }
    }
    bodyRef.current.instanceMatrix.needsUpdate = true;
    headRef.current.instanceMatrix.needsUpdate = true;
    for (let p = 0; p < attire.length; p++) attireRefs.current[p].instanceMatrix.needsUpdate = true;
  });

  return (
    <group>
      <instancedMesh ref={bodyRef} args={[undefined, undefined, count]} castShadow frustumCulled={false}>
        <capsuleGeometry args={[0.32, 1.0, 4, 8]} />
        <meshStandardMaterial vertexColors flatShading roughness={0.85} />
      </instancedMesh>
      <instancedMesh ref={headRef} args={[undefined, undefined, count]} castShadow frustumCulled={false}>
        <sphereGeometry args={[0.32, 8, 6]} />
        <meshStandardMaterial vertexColors flatShading roughness={0.7} />
      </instancedMesh>
      {attire.map((pt, p) => (
        <instancedMesh key={p} ref={(el) => (attireRefs.current[p] = el)} args={[undefined, undefined, count]} frustumCulled={false}>
          <AGeo shape={pt.shape} args={pt.args} />
          <meshStandardMaterial color={pt.color} emissive={pt.emissive || '#000000'} emissiveIntensity={pt.ei || 0} flatShading roughness={0.8} toneMapped={!pt.emissive} />
        </instancedMesh>
      ))}
    </group>
  );
}

// Ideological clusters distributed across the banks and river margins.
export default function Humans() {
  const ws = useStore.getState().worldSeed;
  const r = mulberry32(ws + 31);
  const q = typeof window !== 'undefined' && (new URLSearchParams(window.location.search).get('photo') || new URLSearchParams(window.location.search).get('lowfx') === '1') ? 0.35 : 1;
  const c = (base, span) => Math.max(6, Math.round((base + Math.floor(r() * span)) * q));
  return (
    <group>
      {/* LIBERAL BANK — 50/50 Scholars & Professionals, clustered by clearing */}
      {/* Scholars (academic casual + mortarboards): Healthcare, Education & Sports, Environment & Travel */}
      <Faction count={c(26, 12)} seed={321 + ws} zMin={-92} zMax={-38} bodyColor="#2f6bff" headColor="#dbe6ff" leftBehavior style="scholarly" zoneIdxs={[0, 8, 9]} />
      {/* Professionals (blazers + dress shirts): Economy, Media & Books, Technology */}
      <Faction count={c(26, 12)} seed={322 + ws} zMin={-92} zMax={-38} bodyColor="#93b4ff" headColor="#e8ddc6" leftBehavior style="professional" zoneIdxs={[1, 2, 7]} />
      {/* CONSERVATIVE BANK */}
      {/* Ambivalent Right — inner red bank near free-market zones */}
      <Faction count={c(28, 16)} seed={654 + ws} zMin={30} zMax={58} bodyColor="#f59e0b" headColor="#f5e6c0" style="plain" />
      {/* Faith and Flag (Sentinels) — far red bank, helmets + shields */}
      <Faction count={c(28, 16)} seed={655 + ws} zMin={62} zMax={95} bodyColor="#ef4444" headColor="#f3d0b0" style="sentinel" />
      {/* Stressed Sideliners — the disengaged margins of the neutral river */}
      <Faction count={c(8, 8)} seed={999 + ws} zMin={16} zMax={26} bodyColor="#9ca3af" headColor="#d1d5db" style="plain" />
    </group>
  );
}
